import { FlowEntityType } from '@/services/flow-entities/types'
import { addEdge, applyEdgeChanges, applyNodeChanges, Node } from '@xyflow/react'
import { create } from 'zustand'
import { doLayout } from '../lib/elk'
import { AppState } from '../types/app-state'
import { INodeType } from '../types/wizard-flow'
import { IWizardBlock } from '../types/wizard-types'
import { initialNodes } from './data'

const useStore = create<AppState>((set, get) => ({
    nodes: initialNodes,
    edges: [],
    unmatchedVariables: {},
    onNodesChange: changes => {
        set({
            nodes: applyNodeChanges(changes, get().nodes),
        })
    },
    onEdgesChange: changes => {
        set({
            edges: applyEdgeChanges(changes, get().edges),
        })
    },
    onConnect: connection => {
        set({
            edges: addEdge(connection, get().edges),
        })
    },
    setNodes: nodes => {
        set({ nodes })
    },
    setEdges: edges => {
        set({ edges })
    },
    addNode: node => {
        set({ nodes: [...get().nodes, node] })
    },
    addEdge: edge => {
        set({ edges: [...get().edges, edge] })
    },
    handleSelectNode: (id: string | null) => {
        const nodes = get().nodes.map(node => {
            if (node.data._id === id) {
                return { ...node, selected: true }
            }
            return { ...node, selected: false }
        })
        set({ nodes })
    },
    handleDeselectNodes: () => {
        const nodes = get().nodes.map(node => ({ ...node, selected: false }))
        set({ nodes })
    },
    isNodeSelected: (id: string) => {
        return get().nodes.some(node => node.data._id === id && node.selected)
    },
    getSelectedNode: () => {
        return get().nodes.find(node => node.selected)
    },
    resetStore: () => {
        set({
            nodes: [],
            edges: [],
            unmatchedVariables: {},
        })
    },
    initializeEmptyFlow: () => {
        set({
            nodes: initialNodes,
            edges: [],
        })
    },
    getPreviousNodes: (id: string) => {
        const allNodes = get().nodes
        const currentNode = allNodes.find(node => node.data._id === id)

        const trigger = allNodes.find(node => node.data.type === FlowEntityType.TRIGGER)
        const prevNodes = []

        if (trigger) {
            prevNodes.push(trigger)
        }

        let currentId = trigger?.data.next

        while (currentId) {
            const node = allNodes.find(node => node.data.type !== FlowEntityType.TRIGGER && node.data._id === currentId)
            if (currentNode?.parentId === node?.id && !!node) {
                prevNodes.push(node)
                break
            }
            if (!node || node.data._id === id) break
            prevNodes.push(node)
            currentId = node.data.next
        }

        return prevNodes
    },
    deleteActionBlock: async (id: string) => {
        const allNodes = get().nodes
        const allEdges = get().edges

        const nodeToDelete = allNodes.find(node => node.data._id === id)
        if (!nodeToDelete) return

        let nodesToRemove = [id]
        let updatedNodes = [...allNodes]
        let updatedEdges = [...allEdges]

        if (nodeToDelete.type === INodeType.LOOP) {
            const childNodes = allNodes.filter(node => node.parentId === id)
            nodesToRemove = [...nodesToRemove, ...childNodes.map(node => node.id)]
        }

        const previousNode = allNodes.find(node => node.data.next === nodeToDelete.data._id)
        const nextNodeId = nodeToDelete.data.next
        const nextNode = allNodes.find(node => node.data._id === nextNodeId)

        // Update the next reference of the previous node to point to the next node
        if (previousNode && nextNode) {
            const updatedPreviousNode = {
                ...previousNode,
                data: {
                    ...previousNode.data,
                    next: nextNode.data._id,
                },
            }
            updatedNodes = updatedNodes.map(node => (node.id === previousNode.id ? updatedPreviousNode : node))
        }

        updatedNodes = updatedNodes.filter(node => !nodesToRemove.includes(node.id))
        updatedEdges = updatedEdges.filter(
            edge => !nodesToRemove.includes(edge.source) && !nodesToRemove.includes(edge.target)
        )

        const incomingEdge = allEdges.find(edge => edge.target === id)
        const outgoingEdge = allEdges.find(edge => edge.source === id)

        if (incomingEdge && outgoingEdge) {
            const updatedIncomingEdge = { ...incomingEdge, target: outgoingEdge.target }
            updatedEdges.push(updatedIncomingEdge)
        }

        try {
            const { nodes: layoutedNodes, edges: layoutedEdges } = await doLayout(updatedNodes, updatedEdges)

            const finalNodes = layoutedNodes.map(node => {
                const originalNode = updatedNodes.find(n => n.id === node.id)
                return {
                    ...node,
                    data: {
                        ...node.data,
                        next: originalNode?.data.next || '',
                    },
                }
            })

            set({ nodes: finalNodes, edges: layoutedEdges })
        } catch (error) {
            console.error('Error applying layout:', error)
            set({ nodes: updatedNodes, edges: updatedEdges })
        }
    },
    recalculateLayout: async () => {
        const currentNodes = get().nodes
        const currentEdges = get().edges
        try {
            const { nodes: layoutedNodes, edges: layoutedEdges } = await doLayout(currentNodes, currentEdges)
            set({ nodes: layoutedNodes, edges: layoutedEdges })
        } catch (error) {
            console.error('Error recalculating layout:', error)
        }
    },
    getPreviousBlocksForVariableSelector: (id: string): Node<IWizardBlock>[] => {
        const allNodes = get().nodes
        const prevNodes: Node<IWizardBlock>[] = []

        const currentNode = allNodes.find(node => node.data._id === id)
        if (!currentNode) return prevNodes

        const trigger = allNodes.find(node => node.type === INodeType.TRIGGER)
        if (trigger) {
            prevNodes.push(trigger)
        }

        const getOrderedLoopChildren = (loopNodeId: string): Node<IWizardBlock>[] => {
            const children = allNodes.filter(node => node.parentId === loopNodeId)
            if (children.length === 0) return []

            let firstChild = children.find(child => !children.some(other => other.data.next === child.data._id))

            if (!firstChild && children.length > 0) {
                firstChild = children[0]
            }

            const orderedChildren: Node<IWizardBlock>[] = []
            let currentChild = firstChild

            while (currentChild) {
                orderedChildren.push(currentChild)
                const nextId = currentChild.data.next
                if (!nextId || orderedChildren.some(node => node.data._id === nextId)) break
                currentChild = children.find(child => child.data._id === nextId)
            }

            for (const child of children) {
                if (!orderedChildren.some(node => node.id === child.id)) {
                    orderedChildren.push(child)
                }
            }

            return orderedChildren
        }

        const findPathToNode = (
            startId: string | undefined,
            targetId: string,
            path: Node<IWizardBlock>[] = [],
            visitedNodes: Set<string> = new Set(),
            isLoopChild: boolean = false
        ): Node<IWizardBlock>[] | null => {
            if (!startId || visitedNodes.has(startId)) return null
            visitedNodes.add(startId)

            const node = allNodes.find(node => node.data._id === startId)
            if (!node) return null

            if (
                node.type !== INodeType.PLACEHOLDER_NODE &&
                node.type !== INodeType.PLACEHOLDER_CONDITION &&
                node.type !== INodeType.CONDITION_GROUP &&
                node.type !== INodeType.LOOP
            ) {
                path.push(node)
            }

            // Only add children of loop nodes when NOT finding path to a loop child
            // AND when we're not looking for the loop itself
            if (node.type === INodeType.LOOP && !isLoopChild && startId !== targetId) {
                const loopChildren = allNodes.filter(n => n.parentId === node.id)

                // Only add children that come BEFORE the target node in the loop order
                const orderedChildren = getOrderedLoopChildren(node.id)
                const targetIndex = orderedChildren.findIndex(child => child.data._id === targetId)

                if (targetIndex > -1) {
                    // Target is in this loop, only add previous children
                    const previousChildren = orderedChildren
                        .slice(0, targetIndex)
                        .filter(
                            child =>
                                child.type !== INodeType.PLACEHOLDER_NODE &&
                                child.type !== INodeType.PLACEHOLDER_CONDITION &&
                                child.type !== INodeType.CONDITION_GROUP
                        )
                    path.push(...previousChildren)
                } else {
                    // Target is not in this loop, add all children (for cross-loop visibility)
                    path.push(
                        ...loopChildren.filter(
                            child =>
                                child.type !== INodeType.PLACEHOLDER_NODE &&
                                child.type !== INodeType.PLACEHOLDER_CONDITION &&
                                child.type !== INodeType.CONDITION_GROUP
                        )
                    )
                }
            }

            if (startId === targetId) return path

            if (node.data.next) {
                const result = findPathToNode(node.data.next, targetId, [...path], visitedNodes)
                if (result) return result
            }

            if (node.type === INodeType.CONDITION_GROUP) {
                const conditions = allNodes.filter(n => n.type === INodeType.CONDITION && n.parentId === node.id)

                for (const condition of conditions) {
                    const result = findPathToNode(condition.data._id, targetId, [...path], visitedNodes)
                    if (result) return result

                    if (condition.data.next) {
                        const result = findPathToNode(condition.data.next, targetId, [...path], visitedNodes)
                        if (result) return result
                    }
                }
            }

            if (node.type === INodeType.CONDITION) {
                const nestedGroups = allNodes.filter(
                    n => n.type === INodeType.CONDITION_GROUP && n.parentId === node.id
                )

                for (const group of nestedGroups) {
                    const result = findPathToNode(group.data._id, targetId, [...path], visitedNodes)
                    if (result) return result
                }
            }

            return null
        }

        let result: Node<IWizardBlock>[] = []

        if (currentNode.parentId) {
            const parentNode = allNodes.find(node => node.id === currentNode.parentId)
            if (parentNode?.type === INodeType.LOOP) {
                const pathToLoop = findPathToNode(trigger?.data._id, parentNode.data._id, [], new Set(), true)
                if (pathToLoop) {
                    result.push(...pathToLoop)
                    result.push(parentNode)
                }

                // Use the ordered loop children helper
                const orderedLoopChildren = getOrderedLoopChildren(parentNode.id)

                // Only include children that come before the current node in the order
                const currentNodeIndex = orderedLoopChildren.findIndex(node => node.data._id === currentNode.data._id)

                if (currentNodeIndex > 0) {
                    const previousChildren = orderedLoopChildren
                        .slice(0, currentNodeIndex)
                        .filter(
                            child =>
                                child.type !== INodeType.PLACEHOLDER_NODE &&
                                child.type !== INodeType.PLACEHOLDER_CONDITION &&
                                child.type !== INodeType.CONDITION_GROUP
                        )

                    result.push(...previousChildren)
                }
            } else {
                const path = findPathToNode(trigger?.data._id, currentNode.data._id)
                if (path) {
                    result = path
                }
            }
        } else {
            const path = findPathToNode(trigger?.data._id, currentNode.data._id)
            if (path) {
                result = path
            }
        }

        return result.filter(node => node.data._id !== currentNode.data._id)
    },
    setUnmatchedVariables: variables => set({ unmatchedVariables: variables }),
}))

export const selector = (state: AppState) => ({
    nodes: state.nodes,
    edges: state.edges,
    onNodesChange: state.onNodesChange,
    onEdgesChange: state.onEdgesChange,
    onConnect: state.onConnect,
    addNode: state.addNode,
    addEdge: state.addEdge,
    setNodes: state.setNodes,
    setEdges: state.setEdges,
    handleSelectNode: state.handleSelectNode,
    isNodeSelected: state.isNodeSelected,
    getSelectedNode: state.getSelectedNode,
    handleDeselectNodes: state.handleDeselectNodes,
    resetStore: state.resetStore,
    initializeEmptyFlow: state.initializeEmptyFlow,
    getPreviousNodes: state.getPreviousNodes,
    deleteActionBlock: state.deleteActionBlock,
    recalculateLayout: state.recalculateLayout,
    getPreviousBlocksForVariableSelector: state.getPreviousBlocksForVariableSelector,
    unmatchedVariables: state.unmatchedVariables,
    setUnmatchedVariables: state.setUnmatchedVariables,
})

export default useStore
